import axios from "axios";
import { parseUnits, type WalletClient } from "viem";
import { getTokenAddress, getGatewayContractAddress, TOKEN_CONFIG } from "@/config";
import { TOKEN_IDS, type TokenId } from "@/types";
import { mainnet, arbitrum } from "wagmi/chains";

// Kyber API base URL
const KYBER_API_BASE_URL = "https://aggregator-api.kyberswap.com";
/**
 * Interface for the Kyber API route response
 */
interface KyberRoute {
    data: string;
    routerAddress: string;
}

/**
 * Gets KyberSwap router data for swapping from a source token to the protocol token
 * Implements the V1 API flow:
 * 1. Query routes with GET /api/v1/routes
 * 2. Encode preferred route with POST /api/v1/route/build
 *
 * @param sourceTokenId The token ID of the source token (e.g., USDC)
 * @param amount The amount to swap in human-readable format
 * @param walletClient The wallet client
 * @returns Router data as a hex string
 */
export const getKyberRouterData = async (
    sourceTokenId: TokenId,
    amount: number,
    walletClient: WalletClient,
    outTokenId?: TokenId,
): Promise<{ callData: string; routerAddress: string; routesResponse: any }> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const chainId = walletClient.chain.id;

    // Get source token address from config
    const sourceTokenAddress = getTokenAddress(chainId, sourceTokenId);

    // Get destination token address

    // Get USDC token address
    const outTokenAddress = getTokenAddress(chainId, outTokenId ?? TOKEN_IDS.USDC);

    // Get gateway address to use as recipient
    const gatewayAddress = getGatewayContractAddress(chainId);

    // Convert amount to proper decimal format based on the token
    const amountWithDecimals = parseUnits(`${amount}`, TOKEN_CONFIG[sourceTokenId].decimals).toString();

    try {
        // Get network-specific API endpoint
        const apiUrl = getKyberApiUrlForChain(chainId);

        // Step 1: Query swap route with GET /api/v1/routes
        const { data: routesResponse } = await axios.get(`${apiUrl}/api/v1/routes`, {
            params: {
                tokenIn: sourceTokenAddress,
                tokenOut: outTokenAddress,
                amountIn: amountWithDecimals,
            },
        });

        // Check if routes are available
        if (!routesResponse.data.routeSummary) {
            throw new Error("No routes available for this swap");
        }

        // Step 2: Encode preferred swap route with POST /api/v1/route/build
        const { data: response } = await axios.post(`${apiUrl}/api/v1/route/build`, {
            routeSummary: routesResponse.data.routeSummary,
            sender: gatewayAddress,
            recipient: gatewayAddress, // Use the gateway address as the recipient
            slippageTolerance: 50, // 0.5%
        });

        const route: KyberRoute = response.data;

        // Return the swap data which contains the encoded router data
        return {
            callData: route.data,
            routerAddress: route.routerAddress,
            routesResponse,
        };
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(
                `Failed to get swap quote: ${error.response.status} - ${
                    error.response.data?.message || "Unknown error"
                }`,
            );
        }
        throw new Error("Failed to fetch swap quote from Kyber API");
    }
};

/**
 * Helper function to get the correct Kyber API URL for the given chain
 */
function getKyberApiUrlForChain(chainId: number): string {
    switch (chainId) {
        case mainnet.id: // Ethereum Mainnet
            return `${KYBER_API_BASE_URL}/ethereum`;
        case arbitrum.id: // Arbitrum One
            return `${KYBER_API_BASE_URL}/arbitrum`;
        // Add other networks as needed
        default:
            throw new Error(`Unsupported chain ID for Kyber API: ${chainId}`);
    }
}
